import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Loader2,
  Lock,
} from "lucide-react";
import Header from "../components/HomePage/header";
import Footer from "../components/HomePage/footer";

const passwordRequirements = [
  "At least 8 characters",
  "One uppercase letter",
  "One lowercase letter",
  "One number",
  "One special character",
];

const AlertMessage = ({ type = "error", children }) => (
  <div
    className={`mb-5 flex items-start gap-3 rounded-md border px-4 py-3 text-sm ${
      type === "success"
        ? "border-green-200 bg-green-50 text-green-800"
        : "border-red-200 bg-red-50 text-red-800"
    }`}
  >
    {type === "success" ? (
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
    ) : (
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
    )}
    <p className="font-semibold">{children}</p>
  </div>
);

const PasswordField = ({ id, label, placeholder, value, onChange }) => (
  <div>
    <label
      htmlFor={id}
      className="block text-xs font-semibold uppercase text-gray-500"
    >
      {label}
    </label>
    <div className="relative mt-2">
      <Lock
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
        aria-hidden="true"
      />
      <input
        id={id}
        type="password"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        className="w-full rounded-md border border-slate-200 bg-white px-3 py-3 pl-10 text-sm text-gray-800 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
      />
    </div>
  </div>
);

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // Success or error message
  const [passwordMatchError, setPasswordMatchError] = useState(false); // Error state for password matching
  const [passwordStrengthError, setPasswordStrengthError] = useState(""); // Password strength error message

  // Password strength validation regex
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if passwords match
    if (password !== confirmPassword) {
      setPasswordMatchError(true);
      return;
    } else {
      setPasswordMatchError(false);
    }

    // Check password strength
    if (!passwordRegex.test(password)) {
      setPasswordStrengthError(
        "Password must be at least 8 characters long, with one uppercase letter, one number, and one special character."
      );
      return;
    } else {
      setPasswordStrengthError("");
    }

    setLoading(true);
    setMessage(""); // Clear previous messages
    try {
      const response = await fetch(`/api/reset-password/${token}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error("Failed to reset password");
      }

      setMessage("Password has been reset successfully!");
      setMessageType("success");

      // Redirect to login page after a short delay
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      setMessage("Error resetting password. Please try again.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-gray-700">
      <Header />

      <main className="px-4 py-10 sm:px-6 lg:px-8">
        <section className="mx-auto grid w-full max-w-6xl gap-8 lg:min-h-[640px] lg:grid-cols-[0.9fr,1.1fr] lg:items-center">
          <div className="max-w-xl">
            <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Create a new password
            </h1>
            <p className="mt-3 text-sm leading-6 text-gray-500 sm:text-base">
              Use a strong password to keep your PrepNexis account and practice
              progress protected.
            </p>
            <div className="mt-6 border-t border-slate-200 pt-5">
              <p className="text-sm font-semibold text-gray-900">
                You will return to login after the reset is complete.
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Make sure both password fields match before submitting.
              </p>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:p-8">
            <div className="mb-6 flex items-start gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <KeyRound className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Reset password
                </p>
                <h2 className="mt-1 text-xl font-extrabold text-gray-900 sm:text-2xl">
                  Enter new password
                </h2>
              </div>
            </div>

            {message && (
              <AlertMessage type={messageType}>{message}</AlertMessage>
            )}

            {passwordMatchError && (
              <AlertMessage>Passwords do not match. Please try again.</AlertMessage>
            )}

            {passwordStrengthError && (
              <AlertMessage>{passwordStrengthError}</AlertMessage>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <PasswordField
                id="password"
                label="New password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <PasswordField
                id="confirmPassword"
                label="Confirm password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase text-gray-500">
                  Password requirements
                </p>
                <div className="mt-3 grid gap-2 text-sm text-gray-600 sm:grid-cols-2">
                  {passwordRequirements.map((requirement) => (
                    <div key={requirement} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-purple-500" />
                      <span>{requirement}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-bold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  loading
                    ? "cursor-not-allowed bg-slate-200 text-slate-500 focus:ring-slate-300"
                    : "bg-violet-600 text-white hover:bg-violet-700 focus:ring-violet-500"
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Resetting
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4" aria-hidden="true" />
                    Reset password
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 flex items-center justify-between gap-3 border-t border-slate-200 pt-5">
              <a
                href="/login"
                className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 transition hover:text-purple-900"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Back to login
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ResetPassword;
